import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaChevronDown, FaChevronUp, FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

function getFinYearId() {
  try {
    const direct = localStorage.getItem("finYearId");
    if (direct) return String(direct);
    const admin = JSON.parse(localStorage.getItem("admin") || "null");
    if (admin?.finYearId) return String(admin.finYearId);
  } catch (_) {}
  return "";
}

function tenantHeaders() {
  const finYearId = getFinYearId();
  return finYearId ? { "x-fin-year-id": finYearId } : {};
}

function tenantParams(extra = {}) {
  const finYearId = getFinYearId();
  return finYearId ? { ...extra, finYearId } : extra;
}

/**
 * SUB BRANCH LISTING (Converted from SubBranchListing.aspx/.cs)
 * Search:
 * - Works immediately as local filter on current page rows
 * - Also sends `search` param to API (optional; add server-side search later for full dataset search)
 *
 * Node API expected:
 * GET /api/subbranches?page=1&pageSize=20&search=abc
 * => { page, pageSize, total, rows: [...] }
 */
const SubBranchListing = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  // UI state
  const [showFilters, setShowFilters] = useState(true);

  // Search
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // List state
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination
  const recordsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Debounce search (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 300);
    return () => clearTimeout(t);
  }, [searchText]);

  // ------------------ LISTING API ------------------
  const fetchList = async (page = 1, search = "") => {
    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/subbranches`, {
        headers: tenantHeaders(),
        params: tenantParams({ page, pageSize: recordsPerPage, search }), // search optional
        // withCredentials: true,
      });

      const rows = res.data?.rows || [];
      const total = Number(res.data?.total || 0);

      const mapped = rows.map((row) => ({
        id: row.fld_branchid,
        sNo: row.rowno,
        mainBranchName: row.fld_mainbranchname || "",
        subBranchName: row.fld_BranchName || "",
        subBranchCode: row.fld_Branchcode || "",
        address1: row.fld_Address1 || "",
        address2: row.fld_Address2 || "",
        isMainBranch: !!row.fld_isMainBranch,
      }));

      setRecords(mapped);
      setTotalRecords(total);

      const tp = Math.max(Math.ceil(total / recordsPerPage), 1);
      setTotalPages(tp);
      setCurrentPage(page);
    } catch (err) {
      console.error("List Error:", err);
      toast.error(err.response?.data?.message || "Failed to load sub-branch list");
    } finally {
      setLoading(false);
    }
  };

  // initial load
  useEffect(() => {
    fetchList(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refetch when debounced search changes
  useEffect(() => {
    // reset to page 1 whenever search changes
    fetchList(1, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handlePageChange = (page) => {
    if (!page || page === "..." || page === currentPage) return;
    fetchList(page, debouncedSearch);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      for (let i = 1; i <= maxVisiblePages; i++) pages.push(i);
      pages.push("...");
    }
    return pages;
  };

  // ------------------ NAV ACTIONS ------------------
  const handleAddNew = () => {
    navigate("/adminMaster/SubbranchMaster");
  };

  const handleViewBranch = (branchId) => {
    navigate(`/adminMaster/SubbranchMaster/${branchId}`);
  };

  const handleRights = (branchName) => {
    navigate(`/adminMaster/ModulesAndRightsManagementSubBranch?branchName=${encodeURIComponent(branchName)}`);
  };

  // ------------------ LOCAL FILTER (works even if server ignores `search`) ------------------
  const filteredRecords = useMemo(() => {
    const q = String(searchText || "").trim().toLowerCase();
    if (!q) return records;

    const contains = (v) => String(v || "").toLowerCase().includes(q);

    return records.filter((r) => {
      return (
        contains(r.mainBranchName) ||
        contains(r.subBranchName) ||
        contains(r.subBranchCode) ||
        contains(r.address1) ||
        contains(r.address2) ||
        (q === "yes" && r.isMainBranch) ||
        (q === "no" && !r.isMainBranch)
      );
    });
  }, [records, searchText]);

  return (
    <AdminLayoutWrapper>
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SUB BRANCH LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Sub Branch Listing
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {totalRecords} Records
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Search" : "Show Search"}
              </button>
            </div>
          </div>

          {/* Search */}
          {showFilters && (
            <div
              className={`rounded-xl border mb-6 overflow-hidden ${
                isDark ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <div
                className={`p-4 flex items-center gap-3 ${
                  isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
                }`}
              >
                <div
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border w-full ${
                    isDark ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"
                  }`}
                >
                  <FaSearch className={`${isDark ? "text-gray-300" : "text-gray-600"}`} />
                  <input
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search by branch, sub branch, code, address... (yes/no for IsMainBranch)"
                    className={`w-full outline-none bg-transparent ${
                      isDark ? "text-white placeholder-gray-300" : "text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => setSearchText("")}
                  className={`px-4 py-2 rounded-lg font-semibold border ${
                    isDark
                      ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
                  }`}
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      S.No
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Branch Name
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Sub Branch Name
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Sub Branch Code
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Address1
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Address2
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      IsMainBranch
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">Rights</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td
                        colSpan="8"
                        className={`px-4 py-6 text-center text-sm font-medium ${
                          isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : filteredRecords.length === 0 ? (
                    <tr>
                      <td
                        colSpan="8"
                        className={`px-4 py-6 text-center text-sm font-medium ${
                          isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        No records found
                      </td>
                    </tr>
                  ) : (
                    filteredRecords.map((record, index) => (
                      <tr
                        key={record.id}
                        className={`transition-colors duration-200 ${
                          index % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-gray-50"
                            : isDark
                            ? "bg-gray-900"
                            : "bg-white"
                        }`}
                      >
                        <td
                          className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {record.sNo}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {record.mainBranchName}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 font-medium ${
                            isDark ? "text-blue-300" : "text-blue-600"
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => handleViewBranch(record.id)}
                            className="underline hover:opacity-80"
                            title="Click to view/edit"
                          >
                            {record.subBranchName}
                          </button>
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {record.subBranchCode}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {record.address1}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {record.address2}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {record.isMainBranch ? "Yes" : "No"}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleRights(record.subBranchName)}
                            className={`px-3 py-1 rounded-md text-sm font-semibold shadow hover:shadow-md transition-all ${
                              isDark
                                ? "bg-indigo-700 hover:bg-indigo-600 text-white"
                                : "bg-indigo-600 hover:bg-indigo-700 text-white"
                            }`}
                          >
                            Rights
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex justify-start mt-6">
            <div className="flex gap-1">
              {renderPageNumbers().map((page, index) => (
                <button
                  key={index}
                  onClick={() => handlePageChange(page)}
                  disabled={page === "..."}
                  className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                    page === currentPage
                      ? "bg-blue-600 text-white border-blue-600"
                      : page === "..."
                      ? isDark
                        ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                        : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                      : isDark
                      ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AdminLayoutWrapper>
  );
};

export default SubBranchListing;
